import React, { useState, useMemo, useEffect } from "react"
import { useLocation } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "../components/card"
import { Button } from "../components/button"
import { Progress } from "../components/progress"
import { Check, AlertTriangle, Minus, X, Gauge, Lightbulb, Users, Handshake, Brain, ChevronLeft, ChevronRight, Trophy, Star, CheckCircle2, BarChart3, FileText, RotateCcw, Heart, Shield, Target, Zap } from 'lucide-react'
import { cn } from "../lib/utils"
import Navbar from "../components/Navbar"
import Sidebar from "../components/Sidebar"
import { Badge } from '../components/badge';
import { toast } from 'react-toastify';
import api from "../sever/Api"
import { generateAIReport } from "../sever/Api"
import { notifyTestCompletion } from "../lib/testUtils";
import { ArrowRight } from 'lucide-react';
// Enneagram Personality Test Data
const enneagramTypes = [
  {
    name: "Core Motivations",
    description: "Understanding what drives and motivates you at your core",
    icon: Heart,
    color: "from-red-500 to-pink-600",
    bgColor: "bg-red-50",
    textColor: "text-red-700",
    borderColor: "border-red-200",
    questions: [
      {
        id: "motivations-q1",
        prompt: "When I achieve something, my main satisfaction comes from:",
        statements: [
          { id: "motivations-q1-s1", text: "Being recognized and admired by others", type: "Type 3" },
          { id: "motivations-q1-s2", text: "Knowing I stayed true to my values", type: "Type 1" },
          { id: "motivations-q1-s3", text: "Feeling I've learned something new", type: "Type 5" },
          { id: "motivations-q1-s4", text: "Experiencing personal growth", type: "Type 4" },
        ],
      },
      {
        id: "motivations-q2",
        prompt: "I feel happiest when:",
        statements: [
          { id: "motivations-q2-s1", text: "I'm in a loving, supportive relationship", type: "Type 2" },
          { id: "motivations-q2-s2", text: "My work is well-organized and structured", type: "Type 1" },
          { id: "motivations-q2-s3", text: "I'm praised for my achievements", type: "Type 3" },
          { id: "motivations-q2-s4", text: "I can express my unique personality", type: "Type 4" },
        ],
      },
      {
        id: "motivations-q3",
        prompt: "My biggest personal goal is:",
        statements: [
          { id: "motivations-q3-s1", text: "To be secure and safe", type: "Type 6" },
          { id: "motivations-q3-s2", text: "To be seen as capable and successful", type: "Type 3" },
          { id: "motivations-q3-s3", text: "To deeply understand myself and others", type: "Type 4" },
          { id: "motivations-q3-s4", text: "To gain wisdom and knowledge", type: "Type 5" },
        ],
      },
      {
        id: "motivations-q4",
        prompt: "I'm motivated by:",
        statements: [
          { id: "motivations-q4-s1", text: "Love and connection", type: "Type 2" },
          { id: "motivations-q4-s2", text: "Doing the right thing", type: "Type 1" },
          { id: "motivations-q4-s3", text: "Standing out as the best", type: "Type 3" },
          { id: "motivations-q4-s4", text: "Exploring ideas and possibilities", type: "Type 7" },
        ],
      },
      {
        id: "motivations-q5",
        prompt: "What matters most to me:",
        statements: [
          { id: "motivations-q5-s1", text: "Strong relationships", type: "Type 2" },
          { id: "motivations-q5-s2", text: "Integrity and fairness", type: "Type 1" },
          { id: "motivations-q5-s3", text: "Recognition for my skills", type: "Type 3" },
          { id: "motivations-q5-s4", text: "Discovering truth", type: "Type 5" },
        ],
      },
    ],
  },
  {
    name: " Challenges",
    description: "Understanding what scares and challenges you most",
    icon: Shield,
    color: "from-blue-500 to-indigo-600",
    bgColor: "bg-blue-50",
    textColor: "text-blue-700",
    borderColor: "border-blue-200",
    questions: [
      {
        id: "fears-q1",
        prompt: "My biggest fear is:",
        statements: [
          { id: "fears-q1-s1", text: "Being unloved or rejected", type: "Type 2" },
          { id: "fears-q1-s2", text: "Being wrong or unethical", type: "Type 1" },
          { id: "fears-q1-s3", text: "Being a failure in my career or goals", type: "Type 3" },
          { id: "fears-q1-s4", text: "Being ordinary or without purpose", type: "Type 4" },
        ],
      },
      {
        id: "fears-q2",
        prompt: "I get stressed when:",
        statements: [
          { id: "fears-q2-s1", text: "People leave me out or ignore me", type: "Type 2" },
          { id: "fears-q2-s2", text: "Rules and order are broken", type: "Type 1" },
          { id: "fears-q2-s3", text: "My performance isn't noticed", type: "Type 3" },
          { id: "fears-q2-s4", text: "I feel misunderstood or unseen", type: "Type 4" },
        ],
      },
      {
        id: "fears-q3",
        prompt: "I try to avoid:",
        statements: [
          { id: "fears-q3-s1", text: "Being alone for long periods", type: "Type 2" },
          { id: "fears-q3-s2", text: "Chaos or unpredictability", type: "Type 6" },
          { id: "fears-q3-s3", text: "Losing my image or status", type: "Type 3" },
          { id: "fears-q3-s4", text: "Being emotionally vulnerable", type: "Type 8" },
        ],
      },
      {
        id: "fears-q4",
        prompt: "The thought that bothers me most is:",
        statements: [
          { id: "fears-q4-s1", text: "No one cares about me", type: "Type 2" },
          { id: "fears-q4-s2", text: "I can't control what's happening", type: "Type 6" },
          { id: "fears-q4-s3", text: "I'm not achieving enough", type: "Type 3" },
          { id: "fears-q4-s4", text: "I don't truly know myself", type: "Type 4" },
        ],
      },
      {
        id: "fears-q5",
        prompt: "When I'm afraid:",
        statements: [
          { id: "fears-q5-s1", text: "I seek reassurance from loved ones", type: "Type 2" },
          { id: "fears-q5-s2", text: "I double-check everything", type: "Type 6" },
          { id: "fears-q5-s3", text: "I push myself to prove my worth", type: "Type 3" },
          { id: "fears-q5-s4", text: "I withdraw and reflect", type: "Type 4" },
        ],
      },
    ],
  },
  {
    name: "Behavioral Tendencies",
    description: "Understanding how you typically behave and react",
    icon: Target,
    color: "from-green-500 to-emerald-600",
    bgColor: "bg-green-50",
    textColor: "text-green-700",
    borderColor: "border-green-200",
    questions: [
      {
        id: "behavior-q1",
        prompt: "When I face a challenge, I:",
        statements: [
          { id: "behavior-q1-s1", text: "Reach out to friends for help", type: "Type 2" },
          { id: "behavior-q1-s2", text: "Make a detailed action plan", type: "Type 1" },
          { id: "behavior-q1-s3", text: "Work harder to stand out", type: "Type 3" },
          { id: "behavior-q1-s4", text: "Step back and think deeply", type: "Type 5" },
        ],
      },
      {
        id: "behavior-q2",
        prompt: "In social situations, I:",
        statements: [
          { id: "behavior-q2-s1", text: "Try to make everyone feel welcome", type: "Type 2" },
          { id: "behavior-q2-s2", text: "Follow proper manners and rules", type: "Type 1" },
          { id: "behavior-q2-s3", text: "Highlight my achievements subtly", type: "Type 3" },
          { id: "behavior-q2-s4", text: "Share unique perspectives", type: "Type 4" },
        ],
      },
      {
        id: "behavior-q3",
        prompt: "When under pressure, I tend to:",
        statements: [
          { id: "behavior-q3-s1", text: "Look for emotional support", type: "Type 2" },
          { id: "behavior-q3-s2", text: "Focus on efficiency and order", type: "Type 1" },
          { id: "behavior-q3-s3", text: "Work overtime to prove myself", type: "Type 3" },
          { id: "behavior-q3-s4", text: "Withdraw to analyze quietly", type: "Type 5" },
        ],
      },
      {
        id: "behavior-q4",
        prompt: "My friends often describe me as:",
        statements: [
          { id: "behavior-q4-s1", text: "Caring and warm", type: "Type 2" },
          { id: "behavior-q4-s2", text: "Responsible and disciplined", type: "Type 1" },
          { id: "behavior-q4-s3", text: "Ambitious and driven", type: "Type 3" },
          { id: "behavior-q4-s4", text: "Thoughtful and insightful", type: "Type 4" },
        ],
      },
      {
        id: "behavior-q5",
        prompt: "My first instinct in new situations is to:",
        statements: [
          { id: "behavior-q5-s1", text: "Build relationships", type: "Type 2" },
          { id: "behavior-q5-s2", text: "Learn the rules", type: "Type 6" },
          { id: "behavior-q5-s3", text: "Make a strong impression", type: "Type 3" },
          { id: "behavior-q5-s4", text: "Observe and analyze", type: "Type 5" },
        ],
      },
    ],
  },
  {
    name: "Enneagram Triads ",
    description: "Understanding how you process information and make decisions",
    icon: Brain,
    color: "from-purple-500 to-violet-600",
    bgColor: "bg-purple-50",
    textColor: "text-purple-700",
    borderColor: "border-purple-200",
    questions: [
      {
        id: "triads-q1",
        prompt: "When making a decision, I rely most on:",
        statements: [
          { id: "triads-q1-s1", text: "My feelings and emotional connections", type: "Heart Triad" },
          { id: "triads-q1-s2", text: "What is logically correct", type: "Head Triad" },
          { id: "triads-q1-s3", text: "My instincts and gut reaction", type: "Gut Triad" },
          { id: "triads-q1-s4", text: "Advice from trusted people", type: "Heart Triad" },
        ],
      },
      {
        id: "triads-q2",
        prompt: "When things go wrong, I:",
        statements: [
          { id: "triads-q2-s1", text: "Try to repair relationships first", type: "Heart Triad" },
          { id: "triads-q2-s2", text: "Create a step-by-step plan to fix it", type: "Head Triad" },
          { id: "triads-q2-s3", text: "Act quickly without overthinking", type: "Gut Triad" },
          { id: "triads-q2-s4", text: "Take time to reflect before acting", type: "Head Triad" },
        ],
      },
      {
        id: "triads-q3",
        prompt: "In daily life, I:",
        statements: [
          { id: "triads-q3-s1", text: "Notice how others are feeling", type: "Heart Triad" },
          { id: "triads-q3-s2", text: "Focus on facts and accuracy", type: "Head Triad" },
          { id: "triads-q3-s3", text: "Trust my immediate reaction", type: "Gut Triad" },
          { id: "triads-q3-s4", text: "Weigh both feelings and logic", type: "Head Triad" },
        ],
      },
      {
        id: "triads-q4",
        prompt: "If I'm uncertain, I:",
        statements: [
          { id: "triads-q4-s1", text: "Ask people I trust", type: "Heart Triad" },
          { id: "triads-q4-s2", text: "Research and gather information", type: "Head Triad" },
          { id: "triads-q4-s3", text: "Follow my instincts", type: "Gut Triad" },
          { id: "triads-q4-s4", text: "Wait and see how things unfold", type: "Head Triad" },
        ],
      },
      {
        id: "triads-q5",
        prompt: "When I'm stressed, I:",
        statements: [
          { id: "triads-q5-s1", text: "Seek emotional comfort", type: "Heart Triad" },
          { id: "triads-q5-s2", text: "Organize my surroundings", type: "Head Triad" },
          { id: "triads-q5-s3", text: "Confront the problem directly", type: "Gut Triad" },
          { id: "triads-q5-s4", text: "Retreat into quiet time", type: "Head Triad" },
        ],
      },
    ],
  },
  {
    name: "Wings ",
    description: "Understanding your personality variations and subtypes",
    icon: Zap,
    color: "from-orange-500 to-red-600",
    bgColor: "bg-orange-50",
    textColor: "text-orange-700",
    borderColor: "border-orange-200",
    questions: [
      {
        id: "wings-q1",
        prompt: "I focus most on:",
        statements: [
          { id: "wings-q1-s1", text: "Comfort, health, and stability", type: "Type 9" },
          { id: "wings-q1-s2", text: "Social approval and belonging", type: "Type 6" },
          { id: "wings-q1-s3", text: "Deep one-to-one connections", type: "Type 4" },
          { id: "wings-q1-s4", text: "Personal independence", type: "Type 8" },
        ],
      },
      {
        id: "wings-q2",
        prompt: "In groups, I tend to:",
        statements: [
          { id: "wings-q2-s1", text: "Make sure everyone feels safe", type: "Type 9" },
          { id: "wings-q2-s2", text: "Maintain harmony among members", type: "Type 9" },
          { id: "wings-q2-s3", text: "Form close bonds with specific people", type: "Type 4" },
          { id: "wings-q2-s4", text: "Keep to myself", type: "Type 5" },
        ],
      },
      {
        id: "wings-q3",
        prompt: "My personality feels:",
        statements: [
          { id: "wings-q3-s1", text: "Balanced between two similar types", type: "Wing Type" },
          { id: "wings-q3-s2", text: "Strongly tied to one clear type", type: "Core Type" },
          { id: "wings-q3-s3", text: "A mix of my type and my 'wing' type", type: "Wing Type" },
          { id: "wings-q3-s4", text: "Always shifting depending on mood", type: "Variable Type" },
        ],
      },
      {
        id: "wings-q4",
        prompt: "Under stress, I:",
        statements: [
          { id: "wings-q4-s1", text: "Become more anxious and cautious", type: "Type 6" },
          { id: "wings-q4-s2", text: "Push myself to work harder", type: "Type 3" },
          { id: "wings-q4-s3", text: "Withdraw from others", type: "Type 5" },
          { id: "wings-q4-s4", text: "Become more controlling", type: "Type 8" },
        ],
      },
      {
        id: "wings-q5",
        prompt: "When I feel secure, I:",
        statements: [
          { id: "wings-q5-s1", text: "Relax and enjoy life more", type: "Type 9" },
          { id: "wings-q5-s2", text: "Explore new hobbies or learning", type: "Type 7" },
          { id: "wings-q5-s3", text: "Invest in relationships deeply", type: "Type 2" },
          { id: "wings-q5-s4", text: "Take bold actions", type: "Type 8" },
        ],
      },
    ],
  },
]

export default function EnneagramTest() {
  const location = useLocation();
  const [currentSectionIndex, setCurrentSectionIndex] = useState(0)
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0)
  const [answers, setAnswers] = useState({})
  const [showResults, setShowResults] = useState(false)
  const [showReport, setShowReport] = useState(false)
  const [resultsSaved, setResultsSaved] = useState(false)
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const [aiReportLoading, setAiReportLoading] = useState(false)
  const [aiReportError, setAiReportError] = useState(null)
  const [aiReportText, setAiReportText] = useState("")
  const [showIntro, setShowIntro] = useState(true);
  const [autoNext, setAutoNext] = useState(false);

  const toggleSidebar = () => {
    setSidebarOpen(!sidebarOpen);
  };

  const closeSidebar = () => {
    setSidebarOpen(false);
  };

  const currentCategory = enneagramTypes[currentSectionIndex]
  const currentQuestion = currentCategory.questions[currentQuestionIndex]
  const totalSections = enneagramTypes.length

  const handleAnswerChange = (questionId, statementId, value) => {
    setAnswers((prevAnswers) => ({
      ...prevAnswers,
      [questionId]: {
        [statementId]: value,
      },
    }));
    setAutoNext(true);
  }
  useEffect(() => {
    if (autoNext) {
      if (!isLastQuestion) {
        handleNext();
      }
      setAutoNext(false);
    }
    // eslint-disable-next-line
  }, [autoNext]);

  const totalQuestions = useMemo(() => {
    return enneagramTypes.reduce((acc, category) => acc + category.questions.length, 0)
  }, [])

  const answeredQuestionsCount = useMemo(() => {
    let count = 0
    enneagramTypes.forEach((category) => {
      category.questions.forEach((question) => {
        if (answers[question.id] && Object.values(answers[question.id]).some(value => value === "selected")) {
          count++
        }
      })
    })
    return count
  }, [answers])

  const currentSectionAnsweredCount = useMemo(() => {
    let count = 0
    currentCategory.questions.forEach((question) => {
      if (answers[question.id] && Object.values(answers[question.id]).some(value => value === "selected")) {
        count++
      }
    })
    return count
  }, [answers, currentCategory])

  const sectionScores = useMemo(() => {
    return enneagramTypes.map((category) => {
      let count = 0
      category.questions.forEach((question) => {
        if (answers[question.id] && Object.values(answers[question.id]).some(value => value === "selected")) {
          count++
        }
      })
      return count
    })
  }, [answers])

  const canGoNext = currentSectionIndex < totalSections - 1 || currentQuestionIndex < currentCategory.questions.length - 1
  const canGoPrevious = currentSectionIndex > 0 || currentQuestionIndex > 0
  const isLastQuestion = currentSectionIndex === totalSections - 1 && currentQuestionIndex === currentCategory.questions.length - 1

  // Compute how many statements across the whole quiz map to each enneagram type
  const maxScoreByType = useMemo(() => {
    const counts = {
      "Type 1": 0, "Type 2": 0, "Type 3": 0, "Type 4": 0, "Type 5": 0,
      "Type 6": 0, "Type 7": 0, "Type 8": 0, "Type 9": 0,
      "Heart Triad": 0, "Head Triad": 0, "Gut Triad": 0,
      "Wing Type": 0, "Core Type": 0, "Variable Type": 0
    }
    enneagramTypes.forEach(category => {
      category.questions.forEach(q => {
        q.statements.forEach(s => {
          counts[s.type] = (counts[s.type] || 0) + 1
        })
      })
    })
    return counts
  }, [])

  // Save results when they are first shown
  useEffect(() => {
    if (showResults) {
      saveTestResult();
      try {
        const results = calculateResults;
        if (results && results.length > 0) {
          const top = results[0];
          const totalScore = results.reduce((sum, r) => sum + r.score, 0);
          const totalMaxScore = results.reduce((sum, r) => sum + (maxScoreByType[r.type] || 0), 0);
          const overallPercentage = totalMaxScore > 0 ? Math.round((totalScore / totalMaxScore) * 100) : 0;
          const enneaNum = (top.type || '').match(/Type\s*(\d+)/)?.[1] || '';
          const testData = {
            scores: { personalityType: enneaNum, enneagramType: enneaNum },
            overallScore: overallPercentage,
            overallPercentage,
            testType: 'enneagram',
            completed: true
          };
          notifyTestCompletion('enneagram', testData);
        }
      } catch (e) {
        // no-op
      }
    }
  }, [showResults]);

  // Check for showResults URL parameter
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const showResultsParam = params.get('showResults');
    if (showResultsParam === 'true') {
      setShowResults(true);
    }
  }, [location.search]);

  const overallProgress = (answeredQuestionsCount / totalQuestions) * 100
  const isCurrentQuestionComplete = useMemo(() => {
    if (!answers[currentQuestion.id]) return false;
    return Object.values(answers[currentQuestion.id]).some(value => value === "selected");
  }, [answers, currentQuestion.id])

  const handleNext = () => {
    if (currentQuestionIndex < currentCategory.questions.length - 1) {
      setCurrentQuestionIndex(prev => prev + 1)
    } else if (currentSectionIndex < totalSections - 1) {
      setCurrentSectionIndex(prev => prev + 1)
      setCurrentQuestionIndex(0)
    } else {
      setShowResults(true)
    }
  }

  const handlePrevious = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(prev => prev - 1)
    } else if (currentSectionIndex > 0) {
      setCurrentSectionIndex(prev => prev - 1)
      setCurrentQuestionIndex(enneagramTypes[currentSectionIndex - 1].questions.length - 1)
    }
  }

  const calculateResults = useMemo(() => {
    const scores = {
      "Type 1": 0, "Type 2": 0, "Type 3": 0, "Type 4": 0, "Type 5": 0,
      "Type 6": 0, "Type 7": 0, "Type 8": 0, "Type 9": 0,
      "Heart Triad": 0, "Head Triad": 0, "Gut Triad": 0,
      "Wing Type": 0, "Core Type": 0, "Variable Type": 0
    }

    enneagramTypes.forEach((category) => {
      category.questions.forEach((question) => {
        question.statements.forEach((statement) => {
          const selectedOption = answers[question.id]?.[statement.id]
          if (selectedOption === "selected") {
            scores[statement.type] += 1
          }
        })
      })
    })

    const sortedTypes = Object.entries(scores)
      .map(([type, score]) => ({
        type,
        score,
        category: enneagramTypes.find(cat => cat.name.includes(type) || type.includes(cat.name))
      }))
      .sort((a, b) => b.score - a.score)

    return sortedTypes
  }, [answers])

  const saveTestResult = async () => {
    try {
      const token = localStorage.getItem("token");
      if (!token) {
        toast.error("Please login to save your results.");
        return;
      }

      const results = calculateResults;
      const detailedAnswers = [];

      enneagramTypes.forEach((category) => {
        category.questions.forEach((question) => {
          question.statements.forEach((statement) => {
            const selectedOption = answers[question.id]?.[statement.id];
            if (selectedOption === "selected") {
              detailedAnswers.push({
                questionId: question.id,
                questionText: question.prompt,
                statementId: statement.id,
                statementText: statement.text,
                selectedOptionId: statement.id,
                selectedOptionText: statement.text,
                enneagramType: statement.type,
              });
            }
          });
        });
      });

      // Calculate overall statistics
      const totalScore = results.reduce((sum, result) => sum + result.score, 0);
      const totalMaxScore = results.reduce(
        (sum, result) => sum + maxScoreByType[result.type],
        0
      );
      const overallPercentage = Math.round((totalScore / totalMaxScore) * 100);

      await api.post("/api/test-results", {
        testType: "enneagram",
        answers: detailedAnswers,
        scores: results.reduce((acc, result) => {
          acc[result.type] = result.score;
          return acc;
        }, {}),
        overallScore: totalScore,
        overallPercentage: overallPercentage,
      });

      toast.success("Enneagram Personality Test results saved successfully!");
      setResultsSaved(true);

    } catch (error) {
      console.error("Error saving Enneagram Personality Test result:", error);
      toast.error(
        error.response?.data?.message || "Failed to save test results"
      );
    }
  };

  const getLevel = (percentage) => {
    if (percentage >= 80) return { level: 'Very High', color: 'bg-red-500' }
    if (percentage >= 65) return { level: 'High', color: 'bg-orange-500' }
    if (percentage >= 50) return { level: 'Moderate', color: 'bg-yellow-500' }
    if (percentage >= 35) return { level: 'Low', color: 'bg-blue-500' }
    return { level: 'Very Low', color: 'bg-green-500' }
  }

  const getEnneagramDescription = (type, percentage) => {
    switch (type) {
      case "Type 1":
        return `You are a Perfectionist (Type 1) with a strong desire for order, control, and excellence. Your main motivation is to be seen as competent, reliable, and worthy of respect. You often strive for perfection and perfectionism, which can lead to high standards and a tendency to be critical of yourself and others. Your fear is of being ordinary or unworthy.`;
      case "Type 2":
        return `You are a Helper (Type 2) with a strong need for love, approval, and connection. Your main motivation is to be loved and needed, and you often feel incomplete without a significant other. You are empathetic and caring, often putting others' needs before your own. Your fear is of being unloved or rejected.`;
      case "Type 3":
        return `You are a Achiever (Type 3) with a strong desire for success, recognition, and status. Your main motivation is to be successful and admired, often at the expense of your own well-being. You are ambitious and driven, constantly seeking validation and approval. Your fear is of being a failure.`;
      case "Type 4":
        return `You are a Individualist (Type 4) with a strong need for uniqueness, creativity, and personal expression. Your main motivation is to be seen as special and authentic, often feeling misunderstood or invisible. You are sensitive and introspective, often seeking depth and meaning in your experiences. Your fear is of being ordinary or without purpose.`;
      case "Type 5":
        return `You are a Investigator (Type 5) with a strong need for knowledge, understanding, and control. Your main motivation is to be competent and in control, often seeking mastery and expertise. You are analytical and independent, often preferring to observe from the sidelines. Your fear is of being vulnerable or incompetent.`;
      case "Type 6":
        return `You are a Loyalist (Type 6) with a strong need for security, stability, and safety. Your main motivation is to be secure and safe, often seeking reassurance and protection. You are cautious and risk-averse, often over-analyzing situations. Your fear is of being abandoned or betrayed.`;
      case "Type 7":
        return `You are a Enthusiast (Type 7) with a strong need for variety, excitement, and stimulation. Your main motivation is to experience as much as possible and avoid boredom. You are optimistic and adventurous, often seeking new experiences and thrills. Your fear is of being overwhelmed or bored.`;
      case "Type 8":
        return `You are a Challenger (Type 8) with a strong need for power, control, and dominance. Your main motivation is to be strong and assertive, often seeking to protect and defend. You are competitive and assertive, often taking charge. Your fear is of being weak or powerless.`;
      case "Type 9":
        return `You are a Peacemaker (Type 9) with a strong need for harmony, balance, and security. Your main motivation is to be at peace and avoid conflict, often compromising and avoiding confrontation. You are patient and accommodating, often putting others' needs before your own. Your fear is of being overwhelmed or out of control.`;
      case "Heart Triad":
        return `Your Heart Triad (Type 1, 2, 3) is characterized by a strong desire for love, approval, and connection. You are empathetic and caring, often putting others' needs before your own. You are ambitious and driven, constantly seeking validation and approval. Your fear is of being unloved or rejected.`;
      case "Head Triad":
        return `Your Head Triad (Type 4, 5, 6) is characterized by a strong need for uniqueness, control, and security. You are analytical and independent, often preferring to observe from the sidelines. You are cautious and risk-averse, often over-analyzing situations. Your fear is of being abandoned or betrayed.`;
      case "Gut Triad":
        return `Your Gut Triad (Type 7, 8, 9) is characterized by a strong need for variety, power, and excitement. You are optimistic and adventurous, often seeking new experiences and thrills. You are competitive and assertive, often taking charge. Your fear is of being overwhelmed or out of control.`;
      case "Wing Type":
        return `Your Wing Type (Type 1, 4, 7 or Type 2, 5, 8 or Type 3, 6, 9) is characterized by a strong desire for love, power, or uniqueness. You are empathetic and caring, ambitious and driven, or unique and authentic. Your fear is of being unloved, abandoned, or ordinary.`;
      case "Core Type":
        return `Your Core Type (Type 1, 2, 3, 4, 5, 6, 7, 8, 9) is characterized by a strong desire for love, power, or uniqueness. You are empathetic and caring, ambitious and driven, or unique and authentic. Your fear is of being unloved, abandoned, or ordinary.`;
      case "Variable Type":
        return `Your Variable Type (Type 1, 2, 3, 4, 5, 6, 7, 8, 9) is characterized by a strong desire for love, power, or uniqueness. You are empathetic and caring, ambitious and driven, or unique and authentic. Your fear is of being unloved, abandoned, or ordinary.`;
      default:
        return `Your Enneagram type is ${type}. This type is characterized by a strong desire for love, power, or uniqueness. You are empathetic and caring, ambitious and driven, or unique and authentic. Your fear is of being unloved, abandoned, or ordinary.`;
    }
  };

  const getEnneagramStrengths = (type) => {
    switch (type) {
      case "Type 1":
        return ["Attention to detail", "High standards", "Diligence", "Perfectionism", "Critical thinking"];
      case "Type 2":
        return ["Empathy", "Caring", "Compassion", "Nurturing", "Supportive"];
      case "Type 3":
        return ["Ambition", "Success", "Achievement", "Effort", "Competitiveness"];
      case "Type 4":
        return ["Creativity", "Artistic", "Unique", "Introspective", "Sensitive"];
      case "Type 5":
        return ["Knowledge", "Understanding", "Analytical", "Independent", "Curious"];
      case "Type 6":
        return ["Loyalty", "Dependable", "Responsible", "Protective", "Cautious"];
      case "Type 7":
        return ["Enthusiasm", "Adventurous", "Optimistic", "Exciting", "Spontaneous"];
      case "Type 8":
        return ["Power", "Dominance", "Assertive", "Competitive", "Forceful"];
      case "Type 9":
        return ["Peace", "Harmony", "Patient", "Accommodating", "Calm"];
      case "Heart Triad":
        return ["Empathy", "Caring", "Compassion", "Nurturing", "Supportive"];
      case "Head Triad":
        return ["Critical thinking", "Analytical", "Independent", "Curious", "Sensitive"];
      case "Gut Triad":
        return ["Ambition", "Success", "Achievement", "Effort", "Competitiveness"];
      case "Wing Type":
        return ["Empathy", "Caring", "Compassion", "Nurturing", "Supportive"];
      case "Core Type":
        return ["Critical thinking", "Analytical", "Independent", "Curious", "Sensitive"];
      case "Variable Type":
        return ["Ambition", "Success", "Achievement", "Effort", "Competitiveness"];
      default:
        return ["Strong", "Dedicated", "Adaptable", "Creative", "Independent"];
    }
  };

  const getEnneagramGrowthAreas = (type) => {
    switch (type) {
      case "Type 1":
        return ["Relaxation", "Self-acceptance", "Humor", "Spontaneity", "Playfulness"];
      case "Type 2":
        return ["Independence", "Assertiveness", "Self-reliance", "Self-sufficiency", "Self-confidence"];
      case "Type 3":
        return ["Authenticity", "Simplicity", "Spontaneity", "Relaxation", "Spontaneity"];
      case "Type 4":
        return ["Assertiveness", "Self-reliance", "Self-sufficiency", "Self-confidence", "Self-reliance"];
      case "Type 5":
        return ["Empathy", "Caring", "Compassion", "Nurturing", "Supportive"];
      case "Type 6":
        return ["Assertiveness", "Self-reliance", "Self-sufficiency", "Self-confidence", "Self-reliance"];
      case "Type 7":
        return ["Authenticity", "Simplicity", "Spontaneity", "Relaxation", "Spontaneity"];
      case "Type 8":
        return ["Empathy", "Caring", "Compassion", "Nurturing", "Supportive"];
      case "Type 9":
        return ["Assertiveness", "Self-reliance", "Self-sufficiency", "Self-confidence", "Self-reliance"];
      case "Heart Triad":
        return ["Independence", "Assertiveness", "Self-reliance", "Self-sufficiency", "Self-confidence"];
      case "Head Triad":
        return ["Authenticity", "Simplicity", "Spontaneity", "Relaxation", "Spontaneity"];
      case "Gut Triad":
        return ["Assertiveness", "Self-reliance", "Self-sufficiency", "Self-confidence", "Self-reliance"];
      case "Wing Type":
        return ["Independence", "Assertiveness", "Self-reliance", "Self-sufficiency", "Self-confidence"];
      case "Core Type":
        return ["Authenticity", "Simplicity", "Spontaneity", "Relaxation", "Spontaneity"];
      case "Variable Type":
        return ["Assertiveness", "Self-reliance", "Self-sufficiency", "Self-confidence", "Self-reliance"];
      default:
        return ["Improve communication", "Learn to say no", "Practice self-care", "Take risks", "Be more spontaneous"];
    }
  };

  const getEnneagramCareerPaths = (type) => {
    switch (type) {
      case "Type 1":
        return ["Management", "Consulting", "Research", "Teaching", "Law"];
      case "Type 2":
        return ["Social Work", "Counseling", "Teaching", "Nursing", "Therapy"];
      case "Type 3":
        return ["Sales", "Marketing", "Business Development", "Entrepreneurship", "Finance"];
      case "Type 4":
        return ["Art", "Design", "Writing", "Music", "Film"];
      case "Type 5":
        return ["Research", "Development", "Consulting", "Security", "Investment"];
      case "Type 6":
        return ["Law", "Military", "Emergency Services", "Insurance", "Banking"];
      case "Type 7":
        return ["Sales", "Marketing", "Entertainment", "Sports", "Travel"];
      case "Type 8":
        return ["Law", "Military", "Security", "Investment", "Entrepreneurship"];
      case "Type 9":
        return ["Education", "Non-profit", "Government", "Healthcare", "Social Services"];
      case "Heart Triad":
        return ["Management", "Consulting", "Social Work", "Counseling", "Teaching"];
      case "Head Triad":
        return ["Art", "Design", "Writing", "Music", "Film"];
      case "Gut Triad":
        return ["Sales", "Marketing", "Business Development", "Entrepreneurship", "Finance"];
      case "Wing Type":
        return ["Art", "Design", "Writing", "Music", "Film"];
      case "Core Type":
        return ["Management", "Consulting", "Social Work", "Counseling", "Teaching"];
      case "Variable Type":
        return ["Sales", "Marketing", "Business Development", "Entrepreneurship", "Finance"];
      default:
        return ["Any field", "Any field", "Any field", "Any field", "Any field"];
    }
  };

  const getEnneagramRelationships = (type) => {
    switch (type) {
      case "Type 1":
        return "You are a loyal and committed partner, often seeking a partner who is as ambitious and driven as you. You value honesty and integrity in your relationships.";
      case "Type 2":
        return "You are a caring and nurturing partner, often putting your partner's needs before your own. You value emotional connection and support.";
      case "Type 3":
        return "You are a successful and ambitious partner, often seeking a partner who is as driven and successful as you. You value recognition and admiration.";
      case "Type 4":
        return "You are a unique and authentic partner, often seeking a partner who is as introspective and creative as you. You value depth and meaning in your relationships.";
      case "Type 5":
        return "You are a knowledgeable and independent partner, often seeking a partner who is as analytical and curious as you. You value trust and space.";
      case "Type 6":
        return "You are a loyal and dependable partner, often seeking a partner who is as cautious and risk-averse as you. You value security and stability.";
      case "Type 7":
        return "You are an enthusiastic and adventurous partner, often seeking a partner who is as optimistic and spontaneous as you. You value variety and excitement.";
      case "Type 8":
        return "You are a powerful and assertive partner, often seeking a partner who is as competitive and forceful as you. You value strength and dominance.";
      case "Type 9":
        return "You are a peaceful and accommodating partner, often seeking a partner who is as patient and calm as you. You value harmony and balance.";
      case "Heart Triad":
        return "You are a caring and nurturing partner, often putting your partner's needs before your own. You value emotional connection and support.";
      case "Head Triad":
        return "You are a unique and authentic partner, often seeking a partner who is as introspective and creative as you. You value depth and meaning in your relationships.";
      case "Gut Triad":
        return "You are a successful and ambitious partner, often seeking a partner who is as driven and successful as you. You value recognition and admiration.";
      case "Wing Type":
        return "You are a unique and authentic partner, often seeking a partner who is as introspective and creative as you. You value depth and meaning in your relationships.";
      case "Core Type":
        return "You are a caring and nurturing partner, often putting your partner's needs before your own. You value emotional connection and support.";
      case "Variable Type":
        return "You are a successful and ambitious partner, often seeking a partner who is as driven and successful as you. You value recognition and admiration.";
      default:
        return "You are a loyal and committed partner, often seeking a partner who is as ambitious and driven as you. You value honesty and integrity in your relationships.";
    }
  };

  const getEnneagramPersonalDevelopment = (type) => {
    switch (type) {
      case "Type 1":
        return "Focus on relaxation and self-acceptance. Learn to let go of perfectionism and embrace your imperfections. Practice self-compassion and humor.";
      case "Type 2":
        return "Develop independence and assertiveness. Learn to set boundaries and prioritize your own needs. Practice self-reliance and self-sufficiency.";
      case "Type 3":
        return "Work on authenticity and simplicity. Learn to be genuine and spontaneous. Practice letting go of the need for constant success and recognition.";
      case "Type 4":
        return "Develop assertiveness and self-reliance. Learn to express your needs and desires. Practice self-confidence and self-reliance.";
      case "Type 5":
        return "Focus on empathy and caring. Learn to put yourself in others' shoes and be more compassionate. Practice nurturing and supporting others.";
      case "Type 6":
        return "Develop assertiveness and self-reliance. Learn to set boundaries and prioritize your own needs. Practice self-confidence and self-reliance.";
      case "Type 7":
        return "Work on authenticity and simplicity. Learn to be genuine and spontaneous. Practice letting go of the need for constant success and recognition.";
      case "Type 8":
        return "Focus on empathy and caring. Learn to put yourself in others' shoes and be more compassionate. Practice nurturing and supporting others.";
      case "Type 9":
        return "Develop assertiveness and self-reliance. Learn to set boundaries and prioritize your own needs. Practice self-confidence and self-reliance.";
      case "Heart Triad":
        return "Focus on independence and assertiveness. Learn to set boundaries and prioritize your own needs. Practice self-reliance and self-sufficiency.";
      case "Head Triad":
        return "Work on authenticity and simplicity. Learn to be genuine and spontaneous. Practice letting go of the need for constant success and recognition.";
      case "Gut Triad":
        return "Develop assertiveness and self-reliance. Learn to set boundaries and prioritize your own needs. Practice self-confidence and self-reliance.";
      case "Wing Type":
        return "Focus on independence and assertiveness. Learn to set boundaries and prioritize your own needs. Practice self-reliance and self-sufficiency.";
      case "Core Type":
        return "Focus on independence and assertiveness. Learn to set boundaries and prioritize your own needs. Practice self-reliance and self-sufficiency.";
      case "Variable Type":
        return "Develop assertiveness and self-reliance. Learn to set boundaries and prioritize your own needs. Practice self-confidence and self-reliance.";
      default:
        return "Focus on relaxation and self-acceptance. Learn to let go of perfectionism and embrace your imperfections. Practice self-compassion and humor.";
    }
  };

  if (showIntro) {
    return (
      <div className="min-h-screen w-full overflow-x-hidden bg-gradient-to-br from-[#EFF6FF] via-white to-[#F9FAFB] relative">
        {/* Sidebar */}
        <div className="fixed top-0 left-0 h-screen w-64 bg-white shadow-md z-10">
          <Sidebar />
        </div>

        {/* Main Content */}
        <div className="ml-64">
          <Navbar />

          <div className="w-full max-w-7xl mx-auto pt-10 pb-8 px-4 sm:px-6 lg:px-8">
            <div className="max-w-2xl mx-auto">
              <Card className="text-center bg-white shadow-lg">
                <CardHeader className="space-y-4">
                  <CardTitle className="text-3xl font-bold">Enneagram Finder</CardTitle>
                  <CardDescription className="text-lg text-gray-600">
                    Reflect on your <strong>Core Motivations, Challenges, Behavioral Tendencies,Enneagram Triads and Wings</strong>
                    to gain deeper insights into yourself.
                  </CardDescription>
                </CardHeader>

                <CardContent className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-left">
                    <div className="space-y-2">
                      <h3 className="font-semibold text-purple-600">Core Motivations</h3>
                      <p className="text-sm text-gray-600">What truly drives you</p>
                    </div>
                    <div className="space-y-2">
                      <h3 className="font-semibold text-green-600">Challenges</h3>
                      <p className="text-sm text-gray-600">What obstacles you face</p>
                    </div>
                    <div className="space-y-2">
                      <h3 className="font-semibold text-blue-600">Behavioral Tendencies</h3>
                      <p className="text-sm text-gray-600">How you usually act</p>
                    </div>
                    <div className="space-y-2">
                      <h3 className="font-semibold text-pink-600">Enneagram Triads</h3>
                      <p className="text-sm text-gray-600">Your core emotional style</p>
                    </div>
                    <div className="space-y-2">
                      <h3 className="font-semibold text-pink-600">Wings</h3>
                      <p className="text-sm text-gray-600">The traits that balance you</p>
                    </div>
                  </div>



                  <div className="bg-purple-50 p-4 rounded-lg">
                    <p className="text-sm text-purple-800">
                      <strong>25 questions</strong> • Self-reflection journey
                    </p>
                  </div>

                  <Button
                    onClick={() => setShowIntro(false)}
                    className="w-full text-lg py-6 bg-purple-600 hover:bg-purple-700"
                    size="lg"
                  >
                    Start Reflection <ArrowRight className="ml-2 w-5 h-5" />
                  </Button>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (showResults) {
    if (showReport) {
      // Detailed report view
      return (
        <div className="flex flex-col min-h-screen w-full overflow-x-hidden" style={{ background: 'linear-gradient(90deg, #FFFFFF 0%, #EFF6FF 100%, #F9FAFB 100%)' }}>
          <div className="fixed top-0 left-0 h-screen w-64 bg-white shadow-md z-10">
            <Sidebar isOpen={sidebarOpen} onClose={closeSidebar} />
          </div>
          <div className="ml-0 md:ml-64 min-h-screen">
            <Navbar toggleSidebar={toggleSidebar} />
            <div className="min-h-screen p-4">
              <div className="mx-auto space-y-6" style={{ width: '85%' }}>
                <Card className='bg-[#ffffff]'>
                  <CardHeader>
                    <CardTitle className="text-2xl font-bold flex items-center">
                      <FileText className="mr-3 w-6 h-6" />
                      Detailed Enneagram Personality Report
                    </CardTitle>
                    <CardDescription>Comprehensive analysis of your personality type and traits</CardDescription>
                  </CardHeader>
                </Card>

                <Card className='bg-[#ffffff]'>
                  <CardHeader>
                    <CardTitle className="text-xl font-semibold">We are collecting your results</CardTitle>
                    <CardDescription>Personalized insights</CardDescription>
                  </CardHeader>
                  <CardContent>
                    {aiReportLoading && (
                      <div className="flex items-center text-gray-600">
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600 mr-2"></div>
                        Generating your report...
                      </div>
                    )}
                    {aiReportError && (
                      <div className="text-red-600 text-sm mb-2">{aiReportError}</div>
                    )}
                    {!aiReportLoading && aiReportText && (
                      <div className="prose max-w-none whitespace-pre-wrap text-gray-800 text-sm">{aiReportText}</div>
                    )}
                    {!aiReportLoading && !aiReportText && (
                      <Button onClick={async () => {
                        try {
                          setAiReportLoading(true);
                          setAiReportError(null);
                          const resultsArr = calculateResults;
                          const detailedAnswers = [];
                          enneagramTypes.forEach((category) => {
                            category.questions.forEach((question) => {
                              question.statements.forEach((statement) => {
                                const selectedOption = answers[question.id]?.[statement.id];
                                if (selectedOption === "selected") {
                                  detailedAnswers.push({
                                    questionId: question.id,
                                    questionText: question.prompt,
                                    selectedOptionId: statement.id,
                                    selectedOptionText: statement.text
                                  });
                                }
                              });
                            });
                          });
                          const totalScore = resultsArr.reduce((sum, r) => sum + r.score, 0);
                          const totalMaxScore = resultsArr.reduce((sum, r) => sum + (maxScoreByType[r.type] || 0), 0);
                          const overallPercentage = totalMaxScore > 0 ? Math.round((totalScore / totalMaxScore) * 100) : 0;
                          const payload = {
                            testType: 'enneagram',
                            scores: resultsArr.reduce((acc, r) => { acc[r.type] = r.score; return acc; }, {}),
                            answers: detailedAnswers,
                            overallScore: totalScore,
                            overallPercentage
                          };
                          const { report } = await generateAIReport(payload);
                          setAiReportText(report);
                        } catch (err) {
                          setAiReportError(err?.response?.data?.message || 'Failed to generate AI report');
                        } finally {
                          setAiReportLoading(false);
                        }
                      }} className="bg-blue-600 hover:bg-blue-700 text-white">Generate Report</Button>
                    )}
                  </CardContent>
                </Card>

                {calculateResults.map((result) => {
                  const typeName = result.type
                  const maxPos = maxScoreByType[typeName] || 1
                  const percentage = Math.round((result.score / maxPos) * 100)
                  const { level, color } = getLevel(percentage)
                  return (
                    <Card className='bg-[#ffffff]' key={typeName}>
                      <CardHeader>
                        <div className="flex justify-between items-start">
                          <div>
                            <CardTitle className="text-xl">{typeName}</CardTitle>
                            <CardDescription>{result.category?.description}</CardDescription>
                          </div>
                          <div className="text-right">
                            <div className="text-2xl font-bold">{result.score}/{maxPos}</div>
                            <span className={`px-3 py-1 rounded-full text-sm text-white ${color}`}>{level}</span>
                          </div>
                        </div>
                        <Progress value={percentage} className="h-2" />
                      </CardHeader>
                      <CardContent className="space-y-4">
                        {/* Enhanced Content */}
                        <div className="bg-gray-50 rounded-lg p-4">
                          <h4 className="font-semibold text-gray-800 mb-2">Your Enneagram Profile</h4>
                          <p className="text-gray-700">{getEnneagramDescription(typeName, percentage)}</p>
                        </div>

                        {/* Key Characteristics */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div className="bg-green-50 rounded-lg p-4">
                            <h4 className="font-semibold text-green-800 mb-2">Key Strengths</h4>
                            <ul className="space-y-1">
                              {getEnneagramStrengths(typeName).map((strength, index) => (
                                <li key={index} className="flex items-start gap-2">
                                  <CheckCircle2 className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                                  <span className="text-green-700 text-sm">{strength}</span>
                                </li>
                              ))}
                            </ul>
                          </div>
                          <div className="bg-orange-50 rounded-lg p-4">
                            <h4 className="font-semibold text-orange-800 mb-2">Growth Areas</h4>
                            <ul className="space-y-1">
                              {getEnneagramGrowthAreas(typeName).map((area, index) => (
                                <li key={index} className="flex items-start gap-2">
                                  <AlertTriangle className="w-4 h-4 text-orange-600 mt-0.5 flex-shrink-0" />
                                  <span className="text-orange-700 text-sm">{area}</span>
                                </li>
                              ))}
                            </ul>
                          </div>
                        </div>

                        {/* Career & Relationships */}
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                          <div className="bg-blue-50 rounded-lg p-4">
                            <h4 className="font-semibold text-blue-800 mb-2">Career Paths</h4>
                            <div className="flex flex-wrap gap-2">
                              {getEnneagramCareerPaths(typeName).map((career, index) => (
                                <span key={index} className="px-2 py-1 bg-blue-100 text-blue-800 rounded-full text-xs">
                                  {career}
                                </span>
                              ))}
                            </div>
                          </div>
                          <div className="bg-pink-50 rounded-lg p-4">
                            <h4 className="font-semibold text-pink-800 mb-2">Relationships</h4>
                            <p className="text-pink-700 text-sm leading-relaxed">{getEnneagramRelationships(typeName)}</p>
                          </div>
                        </div>

                        {/* Personal Development */}
                        <div className="bg-emerald-50 rounded-lg p-4">
                          <h4 className="font-semibold text-emerald-800 mb-2">Personal Development</h4>
                          <p className="text-emerald-700 text-sm leading-relaxed">{getEnneagramPersonalDevelopment(typeName)}</p>
                        </div>
                      </CardContent>
                    </Card>
                  )
                })}

                <div className="flex gap-4">
                  <Button onClick={() => setShowReport(false)} variant="outline" className="flex-1 bg-[#ffffff]">
                    Back to Results
                  </Button>
                  <Button
                    onClick={() => {
                      setShowResults(false)
                      setShowReport(false)
                      setCurrentSectionIndex(0)
                      setCurrentQuestionIndex(0)
                      setAnswers({})
                      setResultsSaved(false)
                    }}
                    className="flex-1 bg-purple-600 hover:bg-purple-700 text-white"
                  >
                    Retake Test
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )
    }
    return (
      <div className="flex flex-col min-h-screen w-full overflow-x-hidden" style={{ background: 'linear-gradient(90deg, #FFFFFF 0%, #EFF6FF 100%, #F9FAFB 100%)' }}>
        <div className="fixed top-0 left-0 h-screen w-64 bg-white shadow-md z-10">
          <Sidebar isOpen={sidebarOpen} onClose={closeSidebar} />
        </div>
        <div className="ml-0 md:ml-64 min-h-screen">
          <Navbar toggleSidebar={toggleSidebar} />

          <div className="min-h-screen p-4">
            <div className="mx-auto" style={{ width: '85%' }}>
              <Card className='bg-[#ffffff]'>
                <CardHeader className="text-center">
                  <div className="mx-auto w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mb-4">
                    <Brain className="w-8 h-8 text-purple-600" />
                  </div>
                  <CardTitle className="text-3xl font-bold">Your Enneagram Personality Results</CardTitle>
                  <CardDescription>Based on the Enneagram Personality System</CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  {calculateResults.map((result) => {
                    const typeName = result.type
                    const maxPos = maxScoreByType[typeName] || 1
                    const percentage = Math.round((result.score / maxPos) * 100)
                    const { level, color } = getLevel(percentage)
                    return (
                      <div key={typeName} className="space-y-3">
                        <div className="flex justify-between items-center">
                          <h3 className="font-semibold text-lg">{typeName}</h3>
                          <div className="text-right">
                            <span className="font-bold">{result.score}/{maxPos}</span>
                            <span className={`ml-2 px-2 py-1 rounded text-xs text-white ${color}`}>{level}</span>
                          </div>
                        </div>
                        <Progress value={percentage} className="h-3" />
                        <p className="text-sm text-gray-600">{result.category?.description}</p>
                      </div>
                    )
                  })}
                  <div className="flex gap-4 pt-6">
                    <Button onClick={() => setShowReport(true)} className="flex-1 bg-purple-600 hover:bg-purple-700 text-white">
                      <FileText className="mr-2 w-4 h-4" />
                      View Detailed Report
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => {
                        setShowResults(false)
                        setShowReport(false)
                        setCurrentSectionIndex(0)
                        setCurrentQuestionIndex(0)
                        setAnswers({})
                        setResultsSaved(false)
                      }}
                    >
                      <RotateCcw className="mr-2 w-4 h-4" />
                      Retake Test
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="flex flex-col min-h-screen w-full overflow-x-hidden" style={{ background: 'linear-gradient(90deg, #FFFFFF 0%, #EFF6FF 100%, #F9FAFB 100%)' }}>
      <div className="fixed top-0 left-0 h-screen w-64 bg-white shadow-md z-10">
        <Sidebar isOpen={sidebarOpen} onClose={closeSidebar} />
      </div>
      <div className="ml-0 md:ml-64 min-h-screen " >
        <Navbar
          toggleSidebar={toggleSidebar}
        />
        <div className=" mx-auto" style={{ width: '85%' }}>
          {/* Header */}
          <div className="text-center mb-8 mt-8">
            <h1 className="text-4xl font-bold text-gray-900 mb-2">Enneagram Finder</h1>
            <p className="text-xl text-gray-600">Discover your personality type and understand your core motivations</p>
          </div>

          {/* Progress by Section */}
          <Card className="rounded-lg border text-card-foreground shadow-sm mb-6 bg-[#ffffff] ">
            <CardHeader>
              <CardTitle className="text-lg">Your Progress by Category</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-5 gap-4 mb-6">
                {enneagramTypes.map((section, index) => {
                  const Icon = section.icon
                  return (
                    <div key={section.name} className="text-center">
                      <div className={`w-12 h-12 mx-auto mb-2 rounded-full ${section.bgColor} flex items-center justify-center`}>
                        <Icon className={`w-6 h-6 ${section.textColor}`} />
                      </div>
                      <h3 className="font-medium text-sm mb-1">{section.name}</h3>
                      <p className="text-xs text-gray-500">{sectionScores[index]}/5</p>
                    </div>
                  )
                })}
              </div>
            </CardContent>
          </Card>

          <div className="mb-6">
            <div className="flex items-center justify-between mb-3">
              <span className="text-sm text-gray-600">
                Question {currentSectionIndex * 5 + currentQuestionIndex + 1} of {totalQuestions}
              </span>
              <span className="text-sm text-gray-600">
                {Math.round(overallProgress)}% Complete
              </span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div
                className="bg-purple-600 h-2 rounded-full transition-all duration-300"
                style={{ width: `${overallProgress}%` }}
              />
            </div>
          </div>

          {/* Question Card */}
          <Card className="rounded-lg border text-card-foreground shadow-sm bg-[#ffffff]">
            <CardHeader className="pb-0 border-b">
              <div className="flex items-start gap-4">
                <div className="flex-1">
                  <CardTitle className="text-xl mb-2">
                    {currentSectionIndex + 1}.{currentQuestionIndex + 1} {currentCategory.name}
                  </CardTitle>
                  <p className="text-sm text-gray-500 mb-4">{currentCategory.description}</p>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <h3 className="text-xl font-semibold mb-6 text-gray-900 leading-relaxed mt-3">
                {currentQuestion.prompt}
              </h3>

              <div className="space-y-3">
                {currentQuestion.statements.map((statement, index) => {
                  const isSelected = answers[currentQuestion.id]?.[statement.id] === "selected"
                  return (
                    <div
                      key={statement.id}
                      onClick={() => handleAnswerChange(currentQuestion.id, statement.id, "selected")}
                      className={`flex items-center rounded-xl border-2 p-2 px-4 cursor-pointer transition-all duration-200 ${isSelected
                        ? "border-primary ring-2 ring-primary/50 bg-primary/5 dark:bg-primary/10"
                        : "hover:bg-[rgb(197,199,203)] dark:hover:bg-[rgb(197,199,203)]"
                        }`}
                    >
                      <div className="flex items-center gap-4 w-full">
                        <div className="flex items-center gap-4 w-full">
                          <div className="flex-1">
                            <div >{statement.text}</div>
                          </div>
                        </div>

                      </div>
                    </div>
                  )
                })}
              </div>
            </CardContent>
          </Card>

          {/* Navigation */}
          <div className="flex justify-between items-center m-5">
            <Button
              variant="outline"
              onClick={handlePrevious}
              disabled={!canGoPrevious}
              className="bg-[#ffffff] px-4 py-2 rounded-md text-sm font-medium transition-colors focus:outline-none border border-gray-300 text-gray-700 bg-white hover:bg-gray-100"
            >
              Previous
            </Button>

            {isLastQuestion && (
              <Button
                onClick={handleNext}
                disabled={!isCurrentQuestionComplete}
                className="bg-purple-600 hover:bg-purple-700 text-white flex items-center space-x-2"
              >
                View Results
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
